import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  CreditCard,
  Video,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (credits) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/stripe/create-checkout`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { credits }
        }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Welcome, {user?.name}</h2>
            <p className="text-muted-foreground">Prepare for your university interview</p>
          </div>
          <Button 
            className="bg-lime-500 hover:bg-lime-600 text-white"
            onClick={() => navigate('/pricing')}
            data-testid="buy-credits-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Buy Credits
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card data-testid="credits-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Available Credits</p>
                  <p className="text-3xl font-heading font-bold text-lime-500">
                    {loading ? '-' : dashboardData?.credits || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Interviews Taken</p>
                  <p className="text-3xl font-heading font-bold">
                    {loading ? '-' : dashboardData?.interviews?.length || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Video className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pass Rate</p>
                  <p className="text-3xl font-heading font-bold">
                    {loading ? '-' : (
                      dashboardData?.interviews?.length > 0
                        ? `${Math.round((dashboardData.interviews.filter(i => i.overall_result === 'pass').length / dashboardData.interviews.length) * 100)}%`
                        : '0%'
                    )}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Credit Packs */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Purchase Credits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { credits: 10, price: 9.99 },
                { credits: 25, price: 22.99 },
                { credits: 50, price: 42.99 },
                { credits: 100, price: 79.99 }
              ].map((pack, index) => (
                <Card 
                  key={index} 
                  className="cursor-pointer hover:border-lime-500/50 transition-colors"
                  onClick={() => handleBuyCredits(pack.credits)}
                  data-testid={`credit-pack-${pack.credits}`}
                >
                  <CardContent className="pt-6 text-center">
                    <p className="text-2xl font-heading font-bold text-lime-500">{pack.credits}</p>
                    <p className="text-sm text-muted-foreground mb-2">Credits</p>
                    <p className="font-semibold">${pack.price}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Interview History */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">Interview History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : !dashboardData?.interviews?.length ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No interviews yet</p>
                <p className="text-sm text-muted-foreground">
                  When you receive an interview link, complete the verification process and take your interview.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {dashboardData.interviews.map((interview, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                    data-testid={`interview-history-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      {getStatusIcon(interview.status)}
                      <div>
                        <p className="font-medium">{interview.university_name || 'Interview'}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(interview.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {interview.overall_score !== null && (
                        <Badge variant={interview.overall_result === 'pass' ? 'default' : 'destructive'}>
                          {Math.round(interview.overall_score)}% - {interview.overall_result?.toUpperCase()}
                        </Badge>
                      )}
                      <Badge variant="outline">{interview.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
